import {HostPartialSchema, GenderSchema} from '@/features/authentication/services/auth.schema.ts'
import {z} from 'zod'
import {feedCardVariants} from '@/features/feed/components/feed-card/FeedCard.tsx'
import {CountryWithLanguageSchema, LanguageSchema} from '@/features/country/services/country.schema'
import {HostFeedCardSchemaV3} from '@/features/profile/services/completeProfile.schema'

export const HostFiltersSchema = z.object({
    countries: z.array(CountryWithLanguageSchema),
    languages: z.array(LanguageSchema),
    genders: z.array(GenderSchema),
    fromAge: z.number().nullable(),
    toAge: z.number().nullable()
})

export const FeedPayloadSchemaV3 = z.object({
    limit: z.number().default(50),
    page: z.union([z.number(), z.string()]),
    chatTopicsIds: z.array(z.number()).optional(),
    favoriteActivitiesIds: z.array(z.number()).optional(),
    gendersIds: z.array(z.number()).optional(),
    languagesIds: z.array(z.number()).optional(),
    countriesIds: z.array(z.number()).optional(),
    fromAge: z.number().optional(),
    toAge: z.number().optional(),
    expertisesIds: z.array(z.number()).optional(),
    search: z.string().optional(),
    mural: z.boolean().optional()
})

export const HostFeedResponseSchemaV3 = HostFeedCardSchemaV3

// CARDS
const FeedCardVariants = z.nativeEnum(feedCardVariants)
export const BaseCardContentSchema = z.object({
    cardType: FeedCardVariants
})

export const HostCardContentSchema = BaseCardContentSchema.extend(HostPartialSchema.shape).refine(
    ({id, username}) => username !== undefined && id !== undefined
)

export const FeedTalentsCardSchema = z.object({
    current_page: z.number(),
    data: z.array(HostCardContentSchema),
    first_page_url: z.string(),
    from: z.union([z.number(), z.null()]),
    last_page_url: z.string(),
    next_page_url: z.union([z.string(), z.null()]),
    links: z.array(
        z.object({
            url: z.union([z.string(), z.null()]),
            label: z.string(),
            active: z.boolean()
        })
    ),
    path: z.string(),
    per_page: z.number(),
    prev_page_url: z.number().nullable(),
    to: z.number().nullable(),
    total: z.number().nullable()
})

export const SpecialCardSchema = BaseCardContentSchema.extend({
    index: z.number(),
    id: z.coerce.number()
})

export const FeedCardContentSchema = z.union([HostCardContentSchema, SpecialCardSchema])

export const FeedContextSchema = z.object({
    rookieFeedContext: z.object({
        feeds_count: z.number().nullable(),
        first_feed_type: z.string().nullable().optional(),
        rookieId: z.number().nullable()
    })
})

export const HostStoriesSchema = HostPartialSchema.merge(FeedContextSchema)

export const PhotoSchema = z.object({
    id: z.number(),
    userId: z.number(),
    pathLocation: z.string(),
    main: z.boolean(),
    createdAt: z.string().datetime(),
    underValidation: z.boolean(),
    url: z.string().url(),
    blurHash: z.string().nullable(),
    isAdultContent: z.boolean()
})

export const AvatarSchema = PhotoSchema.extend({
    main: z.boolean()
})

export const PubnubGroupSchema = z.object({
    id: z.number(),
    userId: z.number(),
    name: z.string(),
    category: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
})

export const TypeAttributesSchema = z.object({
    id: z.number(),
    userId: z.number(),
    firstName: z.string(),
    lastName: z.string()
})

export const UserSchema = z.object({
    id: z.number(),
    username: z.string(),
    type: z.string(),
    typeAttributes: TypeAttributesSchema,
    avatar: AvatarSchema.nullable(),
    isOnline: z.boolean(),
    pubnubGroups: PubnubGroupSchema,
    fullName: z.string()
})

export const HostStorySchema = z.object({
    id: z.number(),
    rookieId: z.number().nullable(),
    videoId: z.number().nullable(),
    photoId: z.number().nullable(),
    surpriseId: z.number().nullable(),
    type: z.enum(['photo', 'video']),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    photo: PhotoSchema.nullable(),
    video: z.any().nullable(),
    user: UserSchema
})
